import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useApp } from '../context/AppContext';
import { parseDateInputValue } from '../utils/helpers';
import { createInventoryItem, updateInventoryItem, deleteInventoryItem } from '../services/firestoreService';
import Card from '../components/common/Card';
import PageHero from '../components/common/PageHero';
import Table from '../components/common/Table';
import Button from '../components/common/Button';
import Input from '../components/common/Input';
import Modal from '../components/common/Modal';
import Pagination from '../components/common/Pagination';
import usePagination from '../hooks/usePagination';
import { formatCurrency, formatDate, downloadCsvFile, toMoney } from '../utils/helpers';
import toast from 'react-hot-toast';

// Helper to format dates that could be strings or Firestore Timestamps
const formatItemDate = (value) => {
  if (!value) return '—';
  if (typeof value === 'string') return value;
  if (value?.seconds) {
    return new Date(value.seconds * 1000).toISOString().split('T')[0];
  }
  if (value?.toDate) {
    return value.toDate().toISOString().split('T')[0];
  }
  return '—';
};

const Inventory = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const auditActor = user ? { uid: user.uid, email: user.email } : null;
  const { inventory, loadingInventory } = useApp();
  
  const [showModal, setShowModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  
  // ✅ Updated: Removed 'price', added 'total' for manual entry
  const emptyForm = {
    name: '',
    description: '',
    total: '',        // ✅ Manual total entry
    quantity: '1',
    date: new Date().toISOString().split('T')[0]
  };

  const [formData, setFormData] = useState(emptyForm);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const openAddModal = () => {
    setEditingItem(null);
    setFormData(emptyForm);
    setShowModal(true);
  };

  const openEditModal = (item) => {
    setEditingItem(item);
    setFormData({
      name: item.name || '',
      description: item.description || '',
      total: String(item.total || item.price || ''),  // ✅ Support both old 'price' and new 'total'
      quantity: String(item.quantity || '1'),
      date: formatItemDate(item.date)
    });
    setShowModal(true);
  };

  const openDeleteModal = (item) => {
    setEditingItem(item);
    setShowDeleteModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name.trim()) {
      toast.error('Magaca alaabta waa lagama maarmaan');
      return;
    }
    // ✅ Validate total instead of price
    if (!formData.total || Number(formData.total) < 0) {
      toast.error('Wadarta (Total) waa lagama maarmaan');
      return;
    }
    if (!formData.quantity || Number(formData.quantity) < 1) {
      toast.error('Tirada waa lagama maarmaan');
      return;
    }

    setSubmitting(true);

    // ✅ Prepare data with manual total
    const dataToSave = {
      name: formData.name.trim(),
      description: formData.description.trim(),
      total: Number(formData.total),      // ✅ Save as 'total'
      quantity: Number(formData.quantity),
      date: parseDateInputValue(formData.date),
      auditActor
    };

    let result;
    if (editingItem) {
      result = await updateInventoryItem(editingItem.id, dataToSave);
    } else {
      result = await createInventoryItem(dataToSave);
    }

    if (result.success) {
      toast.success(editingItem ? 'Waa la cusbooneysiiyay' : 'Alaab cusub waa lagu daray');
      setShowModal(false);
    } else {
      toast.error('Khalad: ' + result.error);
    }

    setSubmitting(false);
  };

  const handleAddAnother = async () => {
    if (!formData.name.trim() || !formData.total) {
      toast.error('Fadlan buuxi magaca iyo wadarta');
      return;
    }

    setSubmitting(true);
    
    const dataToSave = {
      name: formData.name.trim(),
      description: formData.description.trim(),
      total: Number(formData.total),
      quantity: Number(formData.quantity),
      date: parseDateInputValue(formData.date),
      auditActor
    };
    
    const result = await createInventoryItem(dataToSave);
    
    if (result.success) {
      toast.success('Waa lagu daray! Ku dar mid kale');
      setFormData({ ...emptyForm, date: formData.date });
    } else {
      toast.error('Khalad: ' + result.error);
    }
    setSubmitting(false);
  };

  const handleDelete = async () => {
    setSubmitting(true);
    const result = await deleteInventoryItem(editingItem.id);
    if (result.success) {
      toast.success('Alaabta waa la tirtiray');
      setShowDeleteModal(false);
    } else {
      toast.error('Khalad: ' + result.error);
    }
    setSubmitting(false);
  };

  // ✅ Updated columns - show 'total' directly
  const columns = [
    {
      key: 'name',
      label: 'Magaca',
      render: (value) => <span className="font-semibold text-gray-900">{value}</span>
    },
    {
      key: 'description',
      label: 'Faahfaahin',
      render: (value) => (
        <span className="text-gray-500 text-sm">{value || '—'}</span>
      )
    },
    {
      key: 'quantity',
      label: 'Tirada',
      render: (value) => (
        <span className="px-2.5 py-1 bg-blue-100 text-blue-700 rounded-full text-sm font-semibold">
          {value || 0}
        </span>
      )
    },
    {
      // ✅ Show manual total (supports both 'total' and legacy 'price')
      key: 'total',
      label: 'Wadarta',
      render: (value, row) => (
        <span className="font-bold text-green-600 text-lg">
          {formatCurrency(value || row.price || 0)}
        </span>
      )
    },
    {
      key: 'date',
      label: 'Taariikhda',
      render: (value) => (
        <span className="text-gray-500 text-sm">{formatItemDate(value)}</span>
      )
    },
    {
      key: 'actions',
      label: '',
      render: (_, row) => (
        <div className="flex gap-2">
          <button
            onClick={(e) => { e.stopPropagation(); openEditModal(row); }}
            className="p-1.5 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
            title="Wax ka bedel"
          >
            ✏️
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); openDeleteModal(row); }}
            className="p-1.5 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
            title="Tirtir"
          >
            🗑️
          </button>
        </div>
      )
    }
  ];

  // ✅ Updated Stats - sum 'total' directly (no multiplication)
  const totalItems = inventory.length;
  const totalQuantity = inventory.reduce((sum, item) => sum + (item.quantity || 0), 0);
  
  // ✅ Sum the manual totals directly
  const totalValue = inventory.reduce((sum, item) => {
    // Support both 'total' (new) and 'price' (legacy/old data)
    const itemTotal = item.total || item.price || 0;
    return sum + Number(itemTotal);
  }, 0);
  
  const avgTotal = totalItems > 0 ? totalValue / totalItems : 0;

  const { page, setPage, pageSize, setPageSize, total, totalPages, pagedItems } = usePagination(
    inventory,
    { persistKey: 'inventory' }
  );

  const exportInventoryCsv = () => {
    if (!inventory.length) {
      toast.error('Alaab ma jirto');
      return;
    }
    const headers = [
      'ID',
      'Magaca',
      'Sharaxaad',
      'Tirada',
      'Wadarta ($)',
      'Taariikh (record)',
      'La sameeyay',
      'User (sameeyay)',
      'La cusbooneysiiyay',
      'User (cusbooneysiiyay)'
    ];
    const rows = inventory.map((item) => {
      const totalVal = toMoney(item.total ?? item.price);
      return [
        item.id,
        item.name,
        item.description || '',
        item.quantity ?? 0,
        totalVal,
        formatItemDate(item.date) === '—' ? '' : formatItemDate(item.date),
        formatDate(item.createdAt),
        item.createdByEmail || item.createdByName || '',
        formatDate(item.updatedAt),
        item.updatedByEmail || item.updatedByName || ''
      ];
    });
    const stamp = new Date().toISOString().slice(0, 10);
    downloadCsvFile(`inventory-all-${stamp}.csv`, headers, rows);
    toast.success(`CSV: ${inventory.length} alaab`);
  };

  return (
    <div className="space-y-4">
      <PageHero
        accent="amber"
        eyebrow="Alaabta"
        title="Inventory"
        subtitle="Maamul alaabta — riix saf si aad u aragto faahfaahin buuxda"
        actions={
          <div className="flex flex-wrap gap-2">
            <Button variant="secondary" onClick={exportInventoryCsv} icon={<span>📥</span>}>
              Soo deji CSV (dhammaan)
            </Button>
            <Button onClick={openAddModal} icon={<span>➕</span>}>
              Ku dar Alaab
            </Button>
          </div>
        }
        stats={[
          { label: 'Alaabta', value: totalItems },
          { label: 'Wadarta lacagta', value: formatCurrency(totalValue) },
          { label: 'Tirada', value: totalQuantity },
          { label: 'Celcelis', value: formatCurrency(avgTotal) }
        ]}
      />

      <Card padding={false}>
        <Table
          columns={columns}
          data={pagedItems}
          loading={loadingInventory}
          emptyMessage="Alaab lama helin — ku dar mid cusub"
          onRowClick={(row) => navigate(`/admin/inventory/${row.id}`)}
        />
        <Pagination
          page={page}
          totalPages={totalPages}
          total={total}
          pageSize={pageSize}
          onPageChange={setPage}
          onPageSizeChange={setPageSize}
        />
      </Card>

      {/* Add/Edit Modal */}
      <Modal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        title={editingItem ? 'Wax ka bedel Alaabta' : 'Ku dar Alaab Cusub'}
      >
        <form onSubmit={handleSubmit} className="space-y-4">
          <Input
            label="Magaca Alaabta"
            name="name"
            value={formData.name}
            onChange={handleChange}
            placeholder="Tusaale: Kaartoon, dhar, etc."
            required
          />
          <Input
            label="Faahfaahin (Optional)"
            name="description"
            value={formData.description}
            onChange={handleChange}
            placeholder="Sharaxaad kooban..."
          />
          
          {/* ✅ Updated: Manual Total Entry */}
          <div className="grid grid-cols-2 gap-4">
            <Input
              label="Tirada"
              name="quantity"
              type="number"
              value={formData.quantity}
              onChange={handleChange}
              placeholder="1"
              required
            />
            {/* ✅ WADARTA - Manual Total Input */}
            <Input
              label="💰 Wadarta (Total $)"
              name="total"
              type="number"
              step="0.01"
              value={formData.total}
              onChange={handleChange}
              placeholder="0.00"
              required
            />
          </div>
          
          {/* ✅ Show the entered total prominently */}
          {formData.total && Number(formData.total) > 0 && (
            <div className="bg-green-50 border border-green-200 rounded-xl p-4 text-center">
              <p className="text-sm text-green-600 mb-1">Wadarta La Geliyay</p>
              <p className="text-2xl font-bold text-green-700">
                {formatCurrency(Number(formData.total))}
              </p>
            </div>
          )}
          
          <Input
            label="Taariikhda"
            name="date"
            type="date"
            value={formData.date}
            onChange={handleChange}
            required
          />
          
          <div className="flex flex-col gap-3 pt-4">
            <div className="flex gap-3">
              <Button type="button" variant="ghost" onClick={() => setShowModal(false)} fullWidth>
                Ka noqo
              </Button>
              <Button type="submit" loading={submitting} fullWidth>
                {editingItem ? 'Kaydi' : 'Ku dar'}
              </Button>
            </div>
            {!editingItem && (
              <Button 
                type="button" 
                variant="secondary"
                onClick={handleAddAnother}
                loading={submitting}
                fullWidth
                icon={<span>➕</span>}
              >
                Kaydi & Ku dar Mid Kale
              </Button>
            )}
          </div>
        </form>
      </Modal>

      {/* Delete Modal */}
      <Modal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        title="Tirtir Alaabta"
        size="sm"
      >
        <div className="text-center">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <span className="text-3xl">📦</span>
          </div>
          <p className="text-gray-600 mb-6">
            Ma hubtaa inaad rabto inaad tirtirto <strong>{editingItem?.name}</strong>?
          </p>
          <div className="flex gap-3">
            <Button variant="ghost" onClick={() => setShowDeleteModal(false)} fullWidth>
              Maya
            </Button>
            <Button variant="danger" onClick={handleDelete} loading={submitting} fullWidth>
              Haa, Tirtir
            </Button>
          </div>
        </div>
      </Modal>

    </div>
  );
};

export default Inventory;